<template>
  <div class="st-surface">
    <StToolbar tone="canvas" class="st-surface__bar">
      <span class="st-surface__title">Battle Tactics</span>
      <span class="st-toolbar-spacer" />
      <FormattingHelp />
    </StToolbar>

    <div class="st-surface__body">
      <!-- Sidebar: one row per battle tactic card (shared rail; reorderable + resizable) -->
      <EditorSideList
        :items="ws.battleTactics.value"
        :selected-index="selectedIndex"
        add-label="Add Battle Tactic"
        empty-hint="No battle tactics yet."
        width-key="studio:tacticsSidebarWidth"
        @select="selectedIndex = $event"
        @add="addCard"
        @reorder="moveCard"
      >
        <template #row="{ item }">{{ item.name || '(unnamed)' }}</template>
      </EditorSideList>

      <!-- Main editor area -->
      <main class="st-surface__main st-surface__main--gap">
        <div v-if="!currentCard" class="st-surface__empty">
          Select or add a battle tactic to begin editing.
        </div>

        <div v-else class="st-editor-card">
          <TitleBar
            :name="currentCard.name"
            noun="Battle Tactic"
            :entity-id="currentCard.id"
            id-prefix="battle-tactic"
            @rename="renameCard"
            @duplicate="duplicateCard"
            @delete="deleteCard"
            @update:entity-id="updateCard({ ...currentCard, id: $event })"
          >
            <template #tabs>
              <StSegmentedControl v-model="currentTab" :options="tabOptions" size="sm" on-shell />
            </template>
          </TitleBar>

          <!-- Editor tab -->
          <div
            v-if="currentTab === 'editor'"
            class="st-editor-card__pane st-editor-card__pane--pad"
          >
            <BattleTacticCardForm :card="currentCard" @update:card="updateCard" />
          </div>

          <!-- Preview tab -->
          <div
            v-else-if="currentTab === 'preview'"
            class="st-editor-card__pane st-editor-card__pane--pad"
          >
            <BattleTacticCard :card="currentCard" />
          </div>
        </div>
      </main>
    </div>

    <NameModal
      :open="adding"
      title="New Battle Tactic"
      placeholder="Battle tactic name…"
      @confirm="handleAdd"
      @close="adding = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import {
  BattleTacticType,
  makeId,
  OtherSuperType,
  OtherTypes,
  type IBattleTactic,
  type IBattleTacticCard,
} from '@scrolldex/shared';
import { StToolbar, StSegmentedControl, type SegmentOption } from '../components/ui/index.js';
import EditorSideList from '../components/EditorSideList.vue';
import TitleBar from '../components/editor/TitleBar.vue';
import BattleTacticCardForm from '../components/BattleTacticCardForm.vue';
import BattleTacticCard from '../components/BattleTacticCard.vue';
import FormattingHelp from '../components/FormattingHelp.vue';
import NameModal from '../components/NameModal.vue';
import { useWorkspace } from '../state/workspace.js';
import { uniqueName } from '../utils/uniqueName.js';
import { useEntityTabs } from '../composables/useEntityTabs.js';
import { useIndexedCollection } from '../composables/useIndexedCollection.js';

const ws = useWorkspace();

const adding = ref(false);
const cards = useIndexedCollection<IBattleTacticCard>({
  items: () => ws.battleTactics.value,
  setItems: (next) => ws.setBattleTactics(next),
});
const {
  selectedIndex,
  current: currentCard,
  updateCurrent,
  append,
  deleteCurrent,
  insertAfterCurrent,
  move,
} = cards;

const tabOptions: SegmentOption[] = [
  { value: 'editor', label: 'Editor', icon: 'file-pen' },
  { value: 'preview', label: 'Preview', icon: 'eye' },
];

// Per-card tab memory keyed by card id (stable across reorder/rename).
const { currentTab, setTab } = useEntityTabs<'editor' | 'preview'>(
  () => currentCard.value?.id,
  'editor',
);

// Globally-unique id for new/duplicated cards (prefix + uuid).
function freshId(): string {
  return makeId('battle-tactic');
}

function newSubTactic(type: BattleTacticType): IBattleTactic {
  return { type, name: '', description: '' };
}

function newCard(name: string): IBattleTacticCard {
  return {
    id: freshId(),
    name,
    type: OtherTypes.BattleTacticCard,
    superType: OtherSuperType,
    text: '',
    affray: newSubTactic(BattleTacticType.Affray),
    strike: newSubTactic(BattleTacticType.Strike),
    domination: newSubTactic(BattleTacticType.Domination),
  };
}

function updateCard(card: IBattleTacticCard) {
  updateCurrent(card);
}

function renameCard(next: string) {
  if (!currentCard.value) return;
  updateCard({ ...currentCard.value, name: next });
}

function addCard() {
  adding.value = true;
}

function handleAdd(name: string) {
  const card = newCard(name);
  append(card);
  setTab(card.id, 'editor');
  adding.value = false;
}

function deleteCard() {
  deleteCurrent();
}

function duplicateCard() {
  const card = currentCard.value;
  if (!card) return;

  const copy: IBattleTacticCard = JSON.parse(JSON.stringify(card));
  copy.id = freshId();
  copy.name = uniqueName(
    card.name,
    ws.battleTactics.value.map((c) => c.name),
  );

  insertAfterCurrent(copy);
  setTab(copy.id, 'editor');
}

function moveCard(from: number, to: number) {
  move(from, to);
}
</script>
